import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Globe } from 'lucide-react';

interface WebsiteMetadataFormProps {
  onSubmit: (websiteUrl: string, shouldFetch: boolean) => void;
  onSkip: () => void;
}

export function WebsiteMetadataForm({ onSubmit, onSkip }: WebsiteMetadataFormProps) {
  const { t } = useTranslation();
  const [websiteUrl, setWebsiteUrl] = useState('');

  const handleSubmit = (e: React.FormEvent, shouldFetch: boolean) => {
    e.preventDefault();
    if (!websiteUrl.trim()) return;
    onSubmit(websiteUrl.trim(), shouldFetch);
  };

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-xl font-semibold text-gray-900">
          {t('brands.websiteFetchModal.title')}
        </h3>
        <p className="mt-2 text-sm text-gray-500">
          {t('brands.websiteFetchModal.description')}
        </p>
      </div>

      <form onSubmit={(e) => handleSubmit(e, true)} className="space-y-4">
        <div>
          <label htmlFor="website" className="sr-only">
            {t('brands.websiteFetchModal.websiteLabel')}
          </label>
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <Globe className="h-5 w-5 text-gray-400" />
            </div>
            <input
              type="text"
              name="website"
              id="website"
              value={websiteUrl}
              onChange={(e) => setWebsiteUrl(e.target.value)}
              className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-primary-500 focus:border-primary-500"
              placeholder={t('brands.websiteFetchModal.websitePlaceholder')}
            />
          </div>
        </div>

        <div className="flex flex-col sm:flex-row gap-3">
          <button
            type="submit"
            disabled={!websiteUrl.trim()}
            className="flex-1 inline-flex justify-center items-center px-4 py-2 border border-transparent text-sm font-medium rounded-lg text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {t('brands.websiteFetchModal.analyze')}
          </button>
          <button
            type="button"
            onClick={(e) => handleSubmit(e, false)}
            disabled={!websiteUrl.trim()}
            className="flex-1 inline-flex justify-center items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-lg text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {t('brands.websiteFetchModal.skipAnalysis')}
          </button>
        </div>

        <div className="text-center">
          <button
            type="button"
            onClick={onSkip}
            className="text-sm text-gray-500 hover:text-gray-700"
          >
            {t('brands.websiteFetchModal.skipAndStart')}
          </button>
        </div>
      </form>
    </div>
  );
}